import { action } from '@ember/object'
import Component from '@glimmer/component'
import { service } from '@ember/service'
import { didCancel } from 'ember-concurrency'
import type StudentImportService from 'teacher-ui-client/services/student-import'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'
import type { Log } from '@blakeelearning/log'
import type { IntlService } from 'ember-intl'
import type RouterService from '@ember/routing/router-service'
import type Rollbar from 'rollbar'
import type { PollingFailedError } from 'teacher-ui-client/services/student-import'
import { ErrorType, isPollingFailedError } from 'teacher-ui-client/services/student-import'
import type ResourceConflictService from 'teacher-ui-client/services/resource-conflict'
import { FidgetLoadingIndicatorComponent } from '@blakeelearning/fidget/components/fidget/loading-indicator'
import { t } from 'ember-intl'
import { UiButtonLink } from 'teacher-ui-client/components/ui-button/link'
import { UiButton } from 'teacher-ui-client/components/ui-button'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import { on } from '@ember/modifier'
import type ValidateRoute from 'teacher-ui-client/manage/import/students/validate/route'
import type Owner from '@ember/owner'
import { InlineAlert, AlertType, InlineAlertSubtitle } from 'teacher-ui-client/components/inline-alert'

interface Args {
  model: ModelFor<ValidateRoute>
}

export class StudentsCsvValidatePage extends Component<Args> {
  @service
  studentImport!: StudentImportService

  @service
  flashQueue!: FlashQueueService

  @service
  log!: Log

  @service
  intl!: IntlService

  @service
  router!: RouterService

  @service
  resourceConflict!: ResourceConflictService

  constructor(owner: Owner, args: Args) {
    super(owner, args)
    void this.validateCsv()
  }

  @action
  async validateCsv() {
    try {
      const result = await this.studentImport.validateCsv.perform(this.args.model.subscriptionType)
      if (result.isOk()) {
        this.flashQueue.clearMessages()
        this.flashQueue.addSuccess({ subtitle: this.intl.t('manage.studentCsv.validateSuccessful') })
        void this.router.replaceWith('manage.import.students.confirm')
      } else if (result.error.errorType === ErrorType.validation) {
        void this.router.replaceWith('manage.import.students.invalid')
      } else if (result.error.errorType === ErrorType.resourceConflict) {
        this.resourceConflict.handleError('validateCsv', result.error)
      } else if (result.error.errorType === ErrorType.missingParam) {
        void this.router.replaceWith('manage.students')
      } else if (isPollingFailedError(result.error)) {
        this.handlePollingFailed(result.error)
      } else {
        this.unexpectedErrorHandler(result.error)
      }
    } catch (error: unknown) {
      if (didCancel(error)) {
        void this.router.replaceWith('manage.students') // should only reject for task cancel (ie, cancel button) or network failure
      } else {
        this.unexpectedErrorHandler(error)
      }
    }
  }

  handlePollingFailed(error: PollingFailedError) {
    const pollingJobId = error.pollingStatus.jobId

    this.flashQueue.clearMessages()
    this.flashQueue.addFail({ subtitle: this.intl.t('manage.studentCsv.validatePollingError') })

    this.log.error(`Polling of student validation status failed. jobId: ${pollingJobId}`)
  }

  unexpectedErrorHandler(error: unknown) {
    this.flashQueue.clearMessages()
    this.flashQueue.addFail({ subtitle: this.intl.t('manage.studentCsv.validateError') })
    const { uuid } = this.log.error(`Unexpected error during student csv validation`, error as Rollbar.LogArgument)
    void this.router.replaceWith('manage.import.students.error', uuid)
  }

  @action
  cancel() {
    this.studentImport.cancelValidateCsv()
    void this.router.replaceWith('manage.students')
  }

  <template>
    <InlineAlert @type={{AlertType.Information}} role="alert" class="mb-8">
      <InlineAlertSubtitle data-test-validate-message>
        {{t "manage.studentCsv.validateStudents"}}
      </InlineAlertSubtitle>
    </InlineAlert>

    <FidgetLoadingIndicatorComponent @show={{true}} @centered={{true}} @overlay={{false}} />

    <div class="mt-8 flex">
      <UiButtonLink class="mr-8 whitespace-nowrap" @appearance={{ButtonAppearance.muted}} {{on "click" this.cancel}}>
        {{t "manage.bulkActions.csv.cancel"}}
      </UiButtonLink>
      {{! a button that is never able to be used, just says uploading... }}
      <UiButton class="whitespace-nowrap" disabled={{true}} @appearance={{ButtonAppearance.regular}}>
        {{t "manage.studentCsv.validateUploading"}}
      </UiButton>
    </div>
  </template>
}

export default StudentsCsvValidatePage
